import { cloneDeep } from "lodash";
import { genKey, getUniqueWorkflowItemIdFromArray } from "../../../utils/utilFunctions";
import { treeActions } from "../../../redux/slices/treeSlice";
import {
  buildCustomUrlQuery,
  customUrlQueryString,
  resolveCustomUrlPath,
} from "./apiServiceApiHelpers";
import { apiT, K } from "./apiServiceI18n";

export const WIZARD_STEPS = {
  CREATE: "create",
  GET_DATA: "getData",
  TRANSLATE_DATA: "translateData",
  SEND_DATA: "sendData",
};

export const SYNC_RESPONSE_ADAPTERS = new Set(["BridgeGateHTTP", "BridgeGateWebService"]);

export function isSyncResponseAdapter(apiType) {
  return SYNC_RESPONSE_ADAPTERS.has(apiType);
}

/** Short template name → stored file name (matches Translate Data properties panel). */
function toTemplateFileName(templateName) {
  if (!templateName) return "";
  return templateName.includes(".") ? templateName : `${templateName}.json`;
}

export function buildWorkflowRoot(workflowName) {
  const name = (workflowName || "").trim() || "UNNAMED";
  return {
    ICON_DISPLAY: "OBJECTS",
    children: [],
    key: "wf-0",
    nodeId: genKey("root"),
    alternateNodeId: genKey("root"),
    state: { opened: true },
    title: name,
    NAME: name,
  };
}

export function buildTemplateHeader(form) {
  return {
    WORKFLOW: {
      ARCHIVE_TRANSACTION_FILES: "TRUE",
      NAME: form.workflowName,
    },
    SERVICE: {
      API_SERVICE_NAME: form.apiName,
      ENABLED: "TRUE",
    },
  };
}

function buildInboundForAdapter(apiType, form) {
  const inbound = {
    ADAPTER_TYPE: apiType,
    ENCODING: "UTF-8",
  };

  if (apiType === "BridgeGateHTTP") {
    const path = resolveCustomUrlPath(form.customUrl);
    if (path) {
      inbound.CUSTOM_URL = path;
      inbound.USE_CUSTOM_URL = "TRUE";
      const queryObj = buildCustomUrlQuery(form.customUrl?.params);
      const queryStr = customUrlQueryString(queryObj);
      if (queryStr) inbound.CUSTOM_URL_QUERY = queryStr;
    }
    inbound.RECEIVE_POST_DATA = "TRUE";
  }

  if (apiType === "BridgeGateWebService") {
    inbound.RECEIVE_POST_DATA = "TRUE";
  }

  return inbound;
}

/** First Get Data node for the wizard — adapter comes from Step 1 apiType. */
export function buildGetDataNode(form, root) {
  const nodes = root?.children?.length ? [root, ...root.children] : [root];
  const newId = getUniqueWorkflowItemIdFromArray(nodes);
  const apiType = form.apiType || "BridgeGateHTTP";

  return {
    key: "wf-0-0",
    title: apiT(K.GET_DATA),
    nodeId: genKey("Get Data"),
    alternateNodeId: genKey("Get Data"),
    ICON_DISPLAY: "GET_DATA",
    ACTION: "GET_DATA",
    ARCHIVE_TRANSACTION_FILES: "TRUE",
    BAM_CONN_MON_ENABLED: "FALSE",
    CONCURRENT: "FALSE",
    ENABLED: "TRUE",
    ID: `${newId}`,
    LOG_WFITEM_DETAILS: "TRUE",
    NAME: apiT(K.API_SVC_GET_DATA_FROM_FILE),
    RUN_ON_SERVER: "LBL_RUN_ON_ALL_ACTIVE_SERVER",
    SYNCHRONOUS: "FALSE",
    INBOUND: buildInboundForAdapter(apiType, form),
  };
}

/** Pre-compute translate / send / outbound IDs when wiring Get Data → Translate → Send Data. */
export function planTranslateSendIds(getDataNode) {
  const siblingNodes = getDataNode ? [getDataNode] : [];
  const translateId = getUniqueWorkflowItemIdFromArray(siblingNodes);
  const sendId = getUniqueWorkflowItemIdFromArray([...siblingNodes, { ID: translateId }]);
  return { translateId, sendId, outboundId: sendId + 1 };
}

export function buildTranslateNode({
  account,
  workflowGroup,
  inboundTemplate,
  outboundTemplate,
  siblingNodes = [],
  getDataNode,
  outboundId,
}) {
  const newId = getUniqueWorkflowItemIdFromArray(siblingNodes);
  const wfg = workflowGroup || "";
  const ib = (inboundTemplate || "").trim();
  const ob = (outboundTemplate || "").trim();
  const inboundFile = toTemplateFileName(ib);
  const outboundFile = toTemplateFileName(ob);

  return {
    key: "wf-0-1",
    title: apiT(K.TRANSLATE_DATA),
    nodeId: genKey("Translate Data"),
    alternateNodeId: genKey("Translate Data"),
    ACTION: "TRANSLATE",
    CONCURRENT: "FALSE",
    ENABLED: "TRUE",
    ICON_DISPLAY: "TRANSLATE",
    ID: `${newId}`,
    LOG_WFITEM_DETAILS: "TRUE",
    NAME: apiT(K.TRANSLATE_DATA),
    ...(getDataNode?.ID != null
      ? {
          INBOUND_ID: `${getDataNode.ID}`,
          INBOUND_NAME: getDataNode.NAME,
        }
      : {}),
    CONDITIONAL_TEMPLATE: [
      {
        NAME: ib || "UNNAMED",
        ...(inboundFile ? { TEMPLATE: inboundFile } : {}),
        FIELDNUM: "0",
        TEMPLATE_LOCATION: `${account}/${wfg}/inbound_templates`,
        RECORD_NUMBER: "0",
        DEFAULT: "FALSE",
        WF_ITEM_DEPENDENCY_TIMEOUT: "5000",
        COMPARE_ACTION_TRIM: "FALSE",
        COMPARE_ACTION_IGNORECASE: "FALSE",
        COMPARE_ACTION_NOT: "FALSE",
        OUTBOUND_CONDITION: [
          {
            NAME: ob || "UNNAMED",
            OUTBOUND_NAME: ob || "UNNAMED",
            ...(outboundId != null ? { OUTBOUND_ID: `${outboundId}` } : {}),
            TEMPLATE_LOCATION: `${account}/${wfg}/outbound_templates`,
            TEMPLATE: outboundFile,
            CONDITION: [],
          },
        ],
      },
    ],
  };
}

export function buildSendDataNode({
  getDataNode,
  syncResponse,
  siblingNodes = [],
  translateNode,
  outboundTemplate,
}) {
  const newId = getUniqueWorkflowItemIdFromArray(siblingNodes);
  const outboundId = newId + 1;
  const adapterType = syncResponse ? "Synchronous Response" : "FILE";
  const translatedOutboundName =
    outboundTemplate ||
    translateNode?.CONDITIONAL_TEMPLATE?.[0]?.OUTBOUND_CONDITION?.[0]?.OUTBOUND_NAME;
  const outboundName = translateNode ? translatedOutboundName || "UNNAMED" : apiT(K.SEND_DATA);

  const node = {
    key: translateNode ? "wf-0-2" : "wf-0-1",
    title: apiT(K.SEND_DATA),
    nodeId: genKey("Send Data"),
    alternateNodeId: genKey("Send Data"),
    ICON_DISPLAY: "SEND_DATA",
    ACTION: "SEND_DATA",
    ARCHIVE_TRANSACTION_FILES: "TRUE",
    CONCURRENT: "FALSE",
    ENABLED: "TRUE",
    ID: `${newId}`,
    LOG_WFITEM_DETAILS: "TRUE",
    NAME: apiT(K.SEND_DATA),
    OUTBOUND_ID: `${outboundId}`,
    OUTBOUND_NAME: outboundName,
    SOURCE_FILE_DO_NOT_DELETE: "FALSE",
    SOURCE_FILE_PRESERVE_PATHS: "FALSE",
    SOURCE_TYPE: translateNode ? "TRANSLATED_DATA" : "FORWARD_ONLY_DATA",
    SYNCHRONOUS: syncResponse ? "TRUE" : "FALSE",
    OUTBOUND: {
      ADAPTER_TYPE: adapterType,
      APPEND_FILE: "FALSE",
      ENCODING: "UTF-8",
      ID: `${outboundId}`,
      ...(translateNode && outboundName ? { OUTBOUND_NAME: outboundName } : {}),
    },
  };

  if (translateNode) {
    node.SOURCE_WFITEM = translateNode.NAME;
    node.SOURCE_WFITEM_ID = `${translateNode.ID}`;
  } else if (getDataNode) {
    node.SOURCE_WFITEM = getDataNode.NAME;
    node.INBOUND_ID = `${getDataNode.ID}`;
    node.INBOUND_NAME = getDataNode.NAME;
  }

  if (syncResponse && getDataNode?.ID) {
    node.SYNC_GET_DATA_ID = getDataNode.ID;
    node.OUTBOUND.SYNC_GET_DATA_ID = getDataNode.ID;
  }

  return node;
}

/** Build body translatedData array for a wizard step. */
export function buildWizardTranslatedData(form, stepConfig) {
  const root = buildWorkflowRoot(form.workflowName || form.apiName);
  const children = [];

  if (stepConfig.includeGetData) {
    const getData = stepConfig.getDataNode || buildGetDataNode(form, root);
    if (stepConfig.syncResponse && isSyncResponseAdapter(form.apiType)) {
      getData.SYNCHRONOUS = "TRUE";
      if (getData.INBOUND) getData.INBOUND.SYNCHRONOUS = "TRUE";
    }
    children.push(getData);
  }

  if (stepConfig.includeTranslate && stepConfig.translateNode) {
    children.push(stepConfig.translateNode);
  }

  if (stepConfig.includeSendData && stepConfig.sendDataNode) {
    children.push(stepConfig.sendDataNode);
  }

  root.children = children;
  reindexWorkflowKeys(root);
  return [root];
}

function reindexWorkflowKeys(root) {
  const walk = (node, prefix) => {
    if (node.children?.length) {
      node.children.forEach((child, idx) => {
        child.key = `${prefix}-${idx}`;
        walk(child, child.key);
      });
    }
  };
  walk(root, "wf-0");
}

export function findWorkflowItemByIcon(translatedData, iconDisplay) {
  const root = translatedData?.[0];
  if (!root?.children) return null;
  return root.children.find((n) => n.ICON_DISPLAY === iconDisplay) || null;
}

export function captureWorkflowTreeSnapshot(treeDataWorkflow) {
  const rt = treeDataWorkflow?.rightTree?.tree;
  return cloneDeep({
    translatedData: rt?.body?.translatedData || [],
    templateHeader: rt?.templateHeader || {},
    selectedNode: rt?.body?.selectedNode || {},
  });
}

export function snapshotWorkflowFromStore(state) {
  return cloneDeep(state.treeData.workflow);
}

export function restoreWorkflowTreeSnapshot(dispatch, snapshot) {
  if (!snapshot) return;
  const wf = snapshot;
  const rt = wf.rightTree?.tree;
  if (!rt) return;

  ["header", "body", "trailer"].forEach((tab) => {
    dispatch(
      treeActions.setNewRightTreeTranslatedData({
        screen: "workflow",
        type: tab,
        data: cloneDeep(rt[tab]?.translatedData || []),
      })
    );
    dispatch(
      treeActions.setRightTreeSelectedNode({
        screen: "workflow",
        type: tab,
        data: cloneDeep(rt[tab]?.selectedNode || {}),
      })
    );
  });

  dispatch(
    treeActions.setRightTreeTemplateHeader({
      screen: "workflow",
      value: cloneDeep(rt.templateHeader || {}),
    })
  );

  dispatch(
    treeActions.setLeftTreeSelectedNode({
      screen: "workflow",
      type: "body",
      data: cloneDeep(wf.leftTree?.tree?.body?.selectedNode || {}),
    })
  );

  dispatch(
    treeActions.setTemplateChanged({
      screen: "workflow",
      data: Boolean(wf.templateChanged),
    })
  );
}

export function applyWizardWorkflowTree(
  dispatch,
  { account, form, translatedData, templateHeader, selectedNode }
) {
  dispatch(treeActions.setCurrentScreen("workflow"));
  dispatch(treeActions.setRightTreeCurrentTab({ screen: "workflow", data: "body" }));

  dispatch(
    treeActions.setLeftTreeSelectedNode({
      screen: "workflow",
      type: "body",
      data: {
        title: form.workflowName,
        workflowGroup: form.workflowGroup,
        account,
        ICON_DISPLAY: "CALL_WORKFLOW",
        key: `wf-${form.workflowGroup}-${form.workflowName}`,
      },
    })
  );

  dispatch(
    treeActions.setNewRightTreeTranslatedData({
      screen: "workflow",
      type: "body",
      data: cloneDeep(translatedData),
    })
  );

  dispatch(
    treeActions.setRightTreeTemplateHeader({
      screen: "workflow",
      value: cloneDeep(templateHeader),
    })
  );

  dispatch(
    treeActions.setRightTreeSelectedNode({
      screen: "workflow",
      type: "body",
      data: cloneDeep(selectedNode),
    })
  );

  dispatch(treeActions.setTemplateChanged({ screen: "workflow", data: true }));
}

export function getTreeItemIds(step, { hasTranslate }) {
  const items = [];
  if (step !== WIZARD_STEPS.CREATE) items.push("getData");
  if (hasTranslate && step !== WIZARD_STEPS.CREATE && step !== WIZARD_STEPS.GET_DATA) {
    items.push("translateData");
  }
  if (step === WIZARD_STEPS.SEND_DATA) items.push("sendData");
  return items;
}

export function getActiveTreeItem(step) {
  switch (step) {
    case WIZARD_STEPS.GET_DATA:
      return "getData";
    case WIZARD_STEPS.TRANSLATE_DATA:
      return "translateData";
    case WIZARD_STEPS.SEND_DATA:
      return "sendData";
    default:
      return null;
  }
}

export function getBreadcrumbSegments(step, { hasTranslate }) {
  const segments = [apiT(K.API_SVC_WIZARD_BREADCRUMB_CREATE)];
  if (step === WIZARD_STEPS.CREATE) return segments;
  segments.push(apiT(K.GET_DATA));
  if (step === WIZARD_STEPS.GET_DATA) return segments;
  if (hasTranslate) {
    segments.push(apiT(K.TRANSLATE_DATA));
    if (step === WIZARD_STEPS.TRANSLATE_DATA) return segments;
    if (step === WIZARD_STEPS.SEND_DATA) segments.push(apiT(K.SEND_DATA));
  } else if (step === WIZARD_STEPS.SEND_DATA) {
    segments.push(apiT(K.SEND_DATA));
  }
  return segments;
}
